import React, { useState } from "react";
import { Phone, Mail, ArrowUpRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import { company, applications } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const fieldCls =
  "bg-white border-brand-line rounded-none text-brand-ink placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-brand-accent h-12";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-2.5 block";

export default function Quote() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject) {
      toast({ title: "Eksik bilgi", description: "Lütfen ad, e-posta ve konu seçimini tamamlayın." });
      return;
    }
    setSending(true);
    setTimeout(() => {
      const saved = JSON.parse(localStorage.getItem("ilka_quotes") || "[]");
      saved.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("ilka_quotes", JSON.stringify(saved));
      setSending(false);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast({ title: "Teklif talebiniz alındı", description: "Ekibimiz en kısa sürede sizinle iletişime geçecektir." });
    }, 700);
  };

  return (
    <div className="bg-white" data-testid="quote-page">
      <PageBanner
        title="Teklif İste"
        subtitle="Özel projeleriniz için detaylı ve rekabetçi teklif alın."
        breadcrumbs={[{ label: "Teklif İste" }]}
        image="/images/slider/slider3.jpg"
      />
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <SectionHeading eyebrow="Teklif Talebi" title="Özel Projeleriniz İçin" />
            <p className="mt-6 text-[14.5px] text-brand-muted leading-[1.85] font-light">
              Çelik konstrüksiyon, basınçlı kaplar, saha tankları, köprü ve viyadükler ve daha fazlası için bizden detaylı teklif
              alabilirsiniz.
            </p>
            <ul className="mt-9 space-y-px bg-brand-line border border-brand-line">
              {["Hızlı geri dönüş", "Dünya standartlarında imalat", "%100 izlenebilirlik", "Rekabetçi fiyatlandırma"].map((b, i) => (
                <li key={i} className="bg-white px-6 py-4 flex items-center gap-4 text-[13.5px] text-brand-ink">
                  <span className="font-mono text-[10px] text-brand-accent/60">{String(i + 1).padStart(2, "0")}</span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-10 space-y-4">
              <a href={company.phoneHref} className="flex items-center gap-3 text-[14px] text-brand-ink hover:text-brand-accent transition-colors duration-300">
                <Phone className="w-4 h-4 text-brand-accent" />
                {company.phone}
              </a>
              <a
                href={`mailto:${company.email}`}
                className="flex items-center gap-3 text-[14px] text-brand-ink hover:text-brand-accent transition-colors duration-300 break-all"
              >
                <Mail className="w-4 h-4 text-brand-accent" />
                {company.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="quote-form" className="bg-brand-paper border border-brand-line p-8 md:p-12">
              <div className="eyebrow text-brand-accent">Teklif Formu</div>
              <h3 className="mt-4 font-display text-xl md:text-2xl font-light text-brand-ink tracking-[-0.03em]">
                Projenizin detaylarını paylaşın
              </h3>
              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Ad - Soyad / Firma *</label>
                  <Input name="name" data-testid="quote-name-input" value={form.name} onChange={onChange} placeholder="Adınız veya firma adınız" className={fieldCls} />
                </div>
                <div>
                  <label className={labelCls}>Telefon</label>
                  <Input name="phone" data-testid="quote-phone-input" value={form.phone} onChange={onChange} placeholder="Telefon numaranız" className={fieldCls} />
                </div>
              </div>
              <div className="mt-6">
                <label className={labelCls}>E-posta *</label>
                <Input name="email" type="email" data-testid="quote-email-input" value={form.email} onChange={onChange} placeholder="ornek@mail.com" className={fieldCls} />
              </div>
              <div className="mt-6">
                <label className={labelCls}>Konu / Uygulama *</label>
                <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                  <SelectTrigger data-testid="quote-subject-select" className="bg-white border-brand-line rounded-none text-brand-ink h-12 focus:ring-0 focus:border-brand-accent">
                    <SelectValue placeholder="Bir uygulama seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-brand-line text-brand-ink max-h-64 rounded-none">
                    {applications.map((a) => (
                      <SelectItem key={a.slug} value={a.title} className="focus:bg-brand-paper focus:text-brand-accent">
                        {a.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Diğer" className="focus:bg-brand-paper focus:text-brand-accent">
                      Diğer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-6">
                <label className={labelCls}>Proje Detayı</label>
                <Textarea
                  name="message"
                  data-testid="quote-message-input"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  placeholder="Projeniz hakkında kısa bilgi verin..."
                  className="bg-white border-brand-line rounded-none text-brand-ink placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-brand-accent"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="quote-submit-btn"
                className="mt-8 group font-mono text-[11px] uppercase tracking-[0.18em] bg-brand-accent hover:bg-brand-accent-dark disabled:opacity-60 text-white pl-6 pr-3.5 py-3.5 rounded-full flex items-center gap-3 transition-colors duration-300"
              >
                {sending ? "Gönderiliyor..." : "Teklif Talebi Gönder"}
                <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
